import { motion } from 'motion/react';

export interface RollingNumberProps {
  /** Target numeric value. Layout is sized from the formatted target
   *  on first paint, so the section width never jumps as digits roll. */
  value: number;
  /** Decimal places — default 2 for currency. */
  fractionDigits?: number;
  /** Roll length for the RIGHTMOST digit (ms). Default 900 ms.
   *  Each slot to its LEFT gets `+ staggerMs`, so the column lands
   *  right-to-left like a slot machine. */
  durationMs?: number;
  /** Extra roll length for every slot to the left of the rightmost
   *  (ms). Default 140 ms. Bigger value → more dramatic
   *  one-at-a-time stop. */
  staggerMs?: number;
  /** How many full 0..9 cycles each digit blurs through before
   *  settling on the target glyph. Higher = wilder spin. Default 6. */
  cycles?: number;
  /** Defer the odometer roll until this flips to `true`. Lets a
   *  parent orchestrator keep the number "parked" at 0 while a
   *  preceding beat plays, then trigger the roll precisely when
   *  the block becomes visible. Default `true` (roll on mount). */
  start?: boolean;
  /** Optional className applied to the outer span. */
  className?: string;
  /** Returning view — skip the slot reels entirely and render the
   *  final formatted value as static text (same outer span + styles).
   *  No roll, no per-digit columns. Default false. */
  instant?: boolean;
}

/** Quick-out, slow-in. Digits blast through their cycles for most
 *  of the duration, then ease into the final glyph for the last
 *  ~30 % — the signature "slot reel slowing down" feel. */
const SLOT_EASE = [0.05, 0.7, 0.2, 1] as const;

/**
 * Slot-machine odometer. Each digit slot is a `1em × 1em` window
 * over a tall column of `cycles + 1` repeats of `0..9`; on roll
 * the column translates UP through every glyph before landing on
 * the target — the user sees a blurry spin that decelerates into
 * the final value.
 *
 * Right-to-left stagger: the rightmost slot has the shortest roll,
 * each slot to its left rolls `staggerMs` longer. All slots start
 * spinning at the same instant, so the column lands one digit at
 * a time, last-to-first.
 *
 * Layout width is fixed from the first paint (the formatted target
 * is rendered immediately) so the section never reflows as digits
 * settle. Non-digit characters (`.`, `,`, `$`) render as static
 * spans inside identical 1em windows so baselines stay flush.
 */
export function RollingNumber({
  value,
  fractionDigits = 2,
  durationMs = 900,
  staggerMs = 140,
  cycles = 6,
  start = true,
  className = '',
  instant = false,
}: RollingNumberProps) {
  if (!Number.isFinite(value)) {
    return <span className={className}>—</span>;
  }
  const formatted = value.toFixed(fractionDigits);

  // Returning view: no reveal to choreograph — just print the final
  // number. Keep the same outer span shape/styles as the rolled path
  // so layout & typography are identical.
  if (instant) {
    return <span className={`inline-flex tabular-nums ${className}`}>{formatted}</span>;
  }

  const chars = [...formatted];

  // Count digit slots first so each one knows its distance from
  // the right edge — that's what drives the staggered duration.
  const totalDigits = chars.reduce(
    (n, ch) => n + (/\d/.test(ch) ? 1 : 0),
    0,
  );

  let digitIndex = -1;

  return (
    <span className={`inline-flex tabular-nums ${className}`}>
      {chars.map((ch, i) => {
        if (/\d/.test(ch)) {
          digitIndex += 1;
          // 0 for rightmost, +1 for each slot to the left.
          const fromRight = totalDigits - 1 - digitIndex;
          const duration = durationMs + fromRight * staggerMs;
          return (
            <RollingDigit
              key={`d-${i}`}
              digit={Number(ch)}
              durationMs={duration}
              cycles={cycles}
              start={start}
            />
          );
        }
        // Wrap static chars in the SAME overflow-hidden 1em window
        // as the digit columns. Without this an inline-block's
        // baseline differs based on `overflow` (text-baseline vs
        // bottom-edge), and the `.` ends up sitting below the digit
        // row. Identical wrappers → identical baselines → flush row.
        return (
          <span
            key={`s-${i}`}
            className="inline-block overflow-hidden"
            style={{ height: '1em', lineHeight: '1em' }}
          >
            {ch}
          </span>
        );
      })}
    </span>
  );
}

interface RollingDigitProps {
  digit: number;
  durationMs: number;
  cycles: number;
  start: boolean;
}

function RollingDigit({ digit, durationMs, cycles, start }: RollingDigitProps) {
  // The column repeats 0..9 `cycles + 1` times (cycles+1 so the
  // final glyph is also there to settle on). Target offset is
  // `cycles` full revolutions (= cycles × 10 em) plus the digit
  // itself — the column travels that far up to bring the target
  // glyph into the window.
  const targetEm = cycles * 10 + digit;
  const stripLength = cycles * 10 + 10;

  return (
    <span
      className="relative inline-block overflow-hidden"
      style={{ height: '1em', lineHeight: '1em' }}
    >
      <motion.span
        className="block"
        initial={{ y: 0 }}
        animate={{ y: start ? `-${targetEm}em` : 0 }}
        transition={{
          duration: durationMs / 1000,
          ease: SLOT_EASE,
        }}
        style={{ willChange: 'transform' }}
      >
        {Array.from({ length: stripLength }, (_, i) => (
          <span
            key={i}
            className="block"
            style={{ height: '1em', lineHeight: '1em' }}
          >
            {i % 10}
          </span>
        ))}
      </motion.span>
    </span>
  );
}
